/**
 * IntegrationsList Component
 * 
 * Third-party integrations display
 * 
 * Version: 6.2.0
 */
import React from 'react'
import './IntegrationsList.css'

interface Integration {
  id: string
  name: string
  description: string
  icon: string
  category: 'broker' | 'data' | 'notification' | 'analysis'
  status: 'connected' | 'disconnected' | 'pending'
  lastSync?: Date
  config?: Record<string, string>
}

interface IntegrationsListProps {
  integrations?: Integration[]
  onConnect?: (id: string) => void
  onDisconnect?: (id: string) => void
  onConfigure?: (id: string) => void
  className?: string
}

// Default integrations
const defaultIntegrations: Integration[] = [
  { 
    id: 'zerodha', 
    name: 'Zerodha', 
    description: 'India\'s largest discount broker with Kite platform',
    icon: '🔴',
    category: 'broker',
    status: 'connected',
    lastSync: new Date('2026-06-13T05:00:00')
  },
  { 
    id: 'angel', 
    name: 'Angel One', 
    description: 'Full-service broker with SmartAPI access',
    icon: '🟡',
    category: 'broker',
    status: 'connected',
    lastSync: new Date('2026-06-13T04:30:00')
  },
  { 
    id: 'twilio', 
    name: 'Twilio', 
    description: 'SMS and WhatsApp notifications',
    icon: '📱',
    category: 'notification',
    status: 'disconnected'
  },
  { 
    id: 'discord', 
    name: 'Discord', 
    description: 'Trade alerts via Discord webhooks',
    icon: '💬',
    category: 'notification',
    status: 'pending'
  },
  { 
    id: 'tradingview', 
    name: 'TradingView', 
    description: 'Advanced charting and analysis',
    icon: '📊',
    category: 'analysis',
    status: 'connected',
    lastSync: new Date('2026-06-13T05:15:00')
  },
  { 
    id: 'nsetools', 
    name: 'NSE Tools', 
    description: 'Real-time NSE/BSE market data',
    icon: '🏛️',
    category: 'data',
    status: 'connected',
    lastSync: new Date('2026-06-13T05:25:00')
  },
]

const IntegrationsList: React.FC<IntegrationsListProps> = ({
  integrations = defaultIntegrations,
  onConnect,
  onDisconnect,
  onConfigure,
  className = ''
}) => {
  const categories = [
    { id: 'broker', label: 'Brokers', icon: '🔗' },
    { id: 'data', label: 'Data Sources', icon: '📡' },
    { id: 'notification', label: 'Notifications', icon: '🔔' },
    { id: 'analysis', label: 'Analysis', icon: '📈' },
  ]

  const getStatusBadge = (status: Integration['status']) => {
    const badges = {
      connected: { label: 'Connected', class: 'connected' },
      disconnected: { label: 'Disconnected', class: 'disconnected' },
      pending: { label: 'Pending', class: 'pending' },
    }
    const badge = badges[status]
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>
  }

  const formatLastSync = (date?: Date) => {
    if (!date) return 'Never'
    const now = new Date()
    const diff = Math.floor((now.getTime() - date.getTime()) / 60000)
    if (diff < 1) return 'Just now'
    if (diff < 60) return `${diff}m ago`
    return `${Math.floor(diff / 60)}h ago`
  }

  const groupedIntegrations = categories.map(cat => ({
    ...cat,
    items: integrations.filter(i => i.category === cat.id)
  })).filter(g => g.items.length > 0)

  return (
    <div className={`integrations-list ${className}`}>
      {groupedIntegrations.map(group => (
        <div key={group.id} className="integration-group">
          <div className="group-header">
            <span className="group-icon">{group.icon}</span>
            <h3 className="group-title">{group.label}</h3>
            <span className="group-count">{group.items.length}</span>
          </div>
          
          <div className="group-items">
            {group.items.map(integration => (
              <div 
                key={integration.id}
                className={`integration-item ${integration.status}`}
              >
                <div className="item-icon">{integration.icon}</div>
                
                <div className="item-info">
                  <div className="item-header">
                    <span className="item-name">{integration.name}</span>
                    {getStatusBadge(integration.status)}
                  </div>
                  <p className="item-description">{integration.description}</p>
                  {integration.lastSync && (
                    <span className="item-sync">
                      Last sync: {formatLastSync(integration.lastSync)}
                    </span>
                  )}
                </div>
                
                <div className="item-actions">
                  {integration.status === 'connected' ? (
                    <>
                      {integration.config && (
                        <button 
                          className="config-btn"
                          onClick={() => onConfigure?.(integration.id)}
                          title="Configure"
                        >
                          ⚙️
                        </button>
                      )}
                      <button 
                        className="disconnect-btn"
                        onClick={() => onDisconnect?.(integration.id)}
                      >
                        Disconnect
                      </button>
                    </>
                  ) : integration.status === 'pending' ? (
                    <button 
                      className="connect-btn pending"
                      onClick={() => onConnect?.(integration.id)}
                    >
                      Complete Setup
                    </button>
                  ) : (
                    <button 
                      className="connect-btn"
                      onClick={() => onConnect?.(integration.id)}
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default IntegrationsList